import React from 'react';
import moment from 'moment';
import cn from 'classnames';

import Link from 'next/link';

import IconTwitter from 'react-scale/components/icons/share/Twitter';
import IconFacebook from 'react-scale/components/icons/share/Facebook';
import IconLinkedin from 'react-scale/components/icons/share/Linkedin';

import { themeDark, themeLight } from './footer-themes';

const productsLinks = [
  { slug: '/data-engine', name: 'Scale Data Engine' },
  { slug: '/generative-ai-platform', name: 'Scale Generative AI Platform' },
  { slug: '/donovan', name: 'Scale Government AI Platform' },
  { slug: '/spellbook', name: 'Scale Spellbook' }
];

const solutionsLinks = [
  { slug: '/retail', name: 'Retail & eCommerce' },
  { slug: '/defense', name: 'Defense' },
  { slug: '/logistics', name: 'Logistics' },
  { slug: '/self-driving-cars', name: 'Autonomous Vehicles' },
  { slug: '/robotics', name: 'Robotics' },
  { slug: '/ar-vr', name: 'AR/VR' },
  { slug: '/content-language', name: 'Content & Language' },
  { slug: '/rlhf', name: 'RLHF' }
];

const companyLinks = [
  { slug: '/about', name: 'About' },
  { slug: '/pricing', name: 'Pricing' },
  // { slug: '/press', name: 'Press' },
  { slug: '/careers', name: 'Careers' },
  { slug: '/security', name: 'Security' },
  { slug: '/legal/terms', name: 'Legal' }
];

const resourcesLinks = [
  { slug: '/blog', name: 'Blog' },
  { slug: '/customers', name: 'Customers' },
  { slug: '/events', name: 'Events' },
  { slug: '/open-av-datasets', name: 'Open Datasets' },
  { slug: '/interviews', name: 'Interviews' },
  { slug: '/docs', name: 'Documentation' },
  { slug: '/guides', name: 'Guides' },
  { slug: 'https://exchange.scale.com/', name: 'Community' },
  { slug: '/ai-readiness-report', name: 'AI Readiness Report 2023' }
];

const guidesLinks = [
  { slug: '/guides/data-labeling-annotation-guide', name: 'Data Labeling' },
  { slug: '/guides/model-training-building', name: 'ML Model Training' },
  { slug: '/guides/diffusion-models-guide', name: 'Diffusion Models' },
  { slug: '/guides/ai-for-ecommerce', name: 'Guide to AI for eCommerce' },
  { slug: '/guides/computer-vision', name: 'Computer Vision Applications' },
  { slug: '/guides/large-language-models', name: 'Large Language Models' }
];

const getResolvedTheme = theme => {
  let resolvedTheme;

  if (typeof theme === 'string') {
    resolvedTheme = theme === 'dark' ? themeDark : themeLight;
  } else {
    resolvedTheme = theme;
  }

  return resolvedTheme;
};

const Footer = ({ theme, border, guides = guidesLinks }) => {
  const resolvedTheme = getResolvedTheme(theme);

  return (
    <footer className={cn({ [`${resolvedTheme.borderColor} border-t`]: border })}>
      <div className="container pt-12 md:pt-20 md:pb-20">
        <nav>
          <ul className="grid grid-cols-2 gap-2 md:gap-10 xl:gap-0 md:grid-cols-3 xl:justify-between xl:flex xl:flex-wrap">
            <Column
              title="Products"
              items={productsLinks}
              textColor={resolvedTheme.textColor}
              textHoverColor={resolvedTheme.textHoverColor}
              width="w-1/2"
            />

            <Column
              title="Solutions"
              items={solutionsLinks}
              textColor={resolvedTheme.textColor}
              textHoverColor={resolvedTheme.textHoverColor}
              width="w-1/2"
            />
            <Column
              title="Company"
              items={companyLinks}
              textColor={resolvedTheme.textColor}
              textHoverColor={resolvedTheme.textHoverColor}
              width="w-1/2"
            />
            <Column
              title="Resources"
              items={resourcesLinks}
              textColor={resolvedTheme.textColor}
              textHoverColor={resolvedTheme.textHoverColor}
              width="w-1/2"
            />
            <Column
              title="Guides"
              items={guides}
              textColor={resolvedTheme.textColor}
              textHoverColor={resolvedTheme.textHoverColor}
              width="w-1/2"
            />
            <Column
              title="Contact"
              textColor={resolvedTheme.textColor}
              textHoverColor={resolvedTheme.textHoverColor}
              width="w-1/2"
            >
              <li className="flex mb-3 text-sm">
                <Link
                  href="/sales"
                  className={`font-normal text-sm ${resolvedTheme.textColor} hover:${resolvedTheme.textHoverColor}`}
                >
                  sales@scale.com
                </Link>
              </li>
              <li className="flex mb-3 text-xs md:text-sm">
                <a
                  href="mailto:support@scale.com"
                  className={`font-normal  ${resolvedTheme.textColor} hover:${resolvedTheme.textHoverColor}`}
                >
                  support@scale.com
                </a>
              </li>
              <li className="flex mb-3 text-xs md:text-sm">
                <a
                  href="mailto:careers@scale.com"
                  className={`font-normal  ${resolvedTheme.textColor} hover:${resolvedTheme.textHoverColor}`}
                >
                  careers@scale.com
                </a>
              </li>
              <li className="flex mb-3 text-xs md:text-sm">
                <a
                  href="mailto:press@scale.com"
                  className={`font-normal  ${resolvedTheme.textColor} hover:${resolvedTheme.textHoverColor}`}
                >
                  press@scale.com
                </a>
              </li>
              <div className={`flex gap-3 mt-8 ${resolvedTheme.textColor}`}>
                <Link href="https://twitter.com/scale_ai">
                  <IconTwitter
                    className={`w-5 h-5 duration-300 ease-in-out fill-current transition-color hover:${resolvedTheme.textHoverColor}`}
                  />
                </Link>
                <Link href="https://www.facebook.com/scaleapi">
                  <IconFacebook
                    className={`w-5 h-5 duration-300 ease-in-out fill-current transition-color hover:${resolvedTheme.textHoverColor}`}
                  />
                </Link>
                <Link href="https://www.linkedin.com/company/scaleai">
                  <IconLinkedin
                    className={`w-5 h-5 duration-300 ease-in-out fill-current transition-color hover:${resolvedTheme.textHoverColor}`}
                  />
                </Link>
              </div>
            </Column>
          </ul>
        </nav>
        <div
          className={`flex text-xs md:text-sm  justify-between flex-col md:flex-row pt-6 md:mt-12 pb-12 ${resolvedTheme.borderColor} border-t`}
        >
          <span className={`${resolvedTheme.textColor}`}>
            Copyright © {moment().year()} Scale AI, Inc. All rights reserved.
          </span>
          <span className={`mt-4 md:mt-0 ${resolvedTheme.textColor}`}>
            <Link
              href="/legal/[slug]"
              as="/legal/terms"
              className={`${resolvedTheme.textColor} font-normal`}
            >
              Terms of Use
            </Link>{' '}
            &amp;{' '}
            <Link
              href="/legal/[slug]"
              as="/legal/privacy"
              className={`${resolvedTheme.textColor} font-normal`}
            >
              Privacy Policy
            </Link>
          </span>
        </div>
      </div>
    </footer>
  );
};

Footer.defaultProps = {
  icon: '/static/images/icon.svg',
  theme: 'dark'
};

const Column = ({ title, items, children, textColor, textHoverColor }) => (
  <li className={` md:w-auto mb-8 md:mb-0`}>
    <h2
      className={`${textColor} text-xs sm:text-sm font-medium mb-4 md:mb-6 uppercase tracking-widest`}
    >
      {title}
    </h2>
    <ul>
      {items &&
        items.map(item => (
          <li className="flex mb-3 text-xs md:text-sm md:mb-3" key={item?.data?.url || item.slug}>
            <Link
              href={item?.data?.url || item.slug}
              className={`font-normal ${textColor} hover:${textHoverColor}`}
            >
              {item?.data?.shortTitle || item.name}
            </Link>
          </li>
        ))}
      {children}
    </ul>
  </li>
);

export default Footer;
